'use client';

import { useState, useEffect } from 'react';
import { Clapperboard, Film } from 'lucide-react';
import RemotionStudio from '@/components/studio/RemotionStudio';
import ResolveStudio from '@/components/studio/ResolveStudio';

type Tab = 'remotion' | 'resolve';

export default function StudioPage() {
  const [activeTab, setActiveTab] = useState<Tab>('remotion');

  // Restore persisted tab after hydration to avoid SSR mismatch
  useEffect(() => {
    const saved = localStorage.getItem('studio-active-tab') as Tab | null;
    if (saved === 'remotion' || saved === 'resolve') setActiveTab(saved);
  }, []);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    localStorage.setItem('studio-active-tab', tab);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3rem)] -m-6">
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-white/10 bg-black/40 shrink-0">
        <button
          onClick={() => switchTab('remotion')}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'remotion'
              ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 shadow-[0_0_8px_rgba(0,240,255,0.15)]'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/5 border border-transparent'
          }`}
        >
          <Clapperboard className="w-4 h-4" />
          Remotion
        </button>
        <button
          onClick={() => switchTab('resolve')}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'resolve'
              ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30 shadow-[0_0_8px_rgba(255,165,0,0.15)]'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/5 border border-transparent'
          }`}
        >
          <Film className="w-4 h-4" />
          DaVinci Resolve
        </button>
      </div>

      {/* Tab content — both rendered, inactive hidden to preserve state */}
      <div className={`flex-1 min-h-0 ${activeTab === 'remotion' ? '' : 'hidden'}`}>
        <RemotionStudio />
      </div>
      <div className={`flex-1 min-h-0 ${activeTab === 'resolve' ? '' : 'hidden'}`}>
        <ResolveStudio />
      </div>
    </div>
  );
}
